import { useRecoilValue } from 'recoil';
import { Color, isColor } from '../enums/Color.enum';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { ViewProduct } from '../models/ViewProduct.model';
import { isShortSize, ShortSize } from '../product_detail/components/size.enum';
import { isMobileState } from '../recoil/selectors';

declare const window: HoneyloveWindow;

export enum Direction {
  Front = 'Front',
  Side = 'Side',
  Back = 'Back',
}

const isDirection = (maybeDirection: unknown): maybeDirection is Direction => Object.values(Direction).includes(maybeDirection as Direction);

enum PhotoType {
  Model = 'Model',
  FlatLay = 'FlatLay',
  Render = 'Render',
  Mannequin = 'Mannequin',
  Lifestyle = 'Lifestyle',
  ProductFeature = 'ProductFeature',
  Fabric = 'Fabric',
  ZoomIn = 'ZoomIn',
}

const isPhotoType = (maybePhotoType: unknown): maybePhotoType is PhotoType => Object.values(PhotoType).includes(maybePhotoType as PhotoType);

export enum ModelName {
  AJ = 'AJ',
  Alex = 'Alex',
  Anita = 'Anita',
  Ashley = 'Ashley',
  Autym = 'Autym',
  Baillie = 'Baillie',
  Bella = 'Bella',
  Brianna = 'Brianna',
  Courtney = 'Courtney',
  Elle = 'Elle',
  Emma = 'Emma',
  Frankie = 'Frankie',
  Grace = 'Grace',
  Hawa = 'Hawa',
  Hayley = 'Hayley',
  Jade = 'Jade',
  Katie = 'Katie',
  Larissa = 'Larissa',
  McCallah = 'McCallah',
  Natalie = 'Natalie',
  Nicki = 'Nicki',
  Olivia = 'Olivia',
  Hannah = 'Hannah',
  Michelle = 'Michelle',
  Shelly = 'Shelly',
  Yumi = 'Yumi',
}

const isModelName = (maybeModelName: unknown): maybeModelName is ModelName => Object.values(ModelName).includes(maybeModelName as ModelName);

interface PhotoBase {
  src: string;
  color: Color | undefined;
}

export type ModelPhoto = PhotoBase & {
  type: 'Model';
  modelName: ModelName;
  direction: Direction;
  size: ShortSize;
};

export type MannequinPhoto = PhotoBase & {
  type: 'Mannequin';
  direction: Direction;
};

type FlatLayPhoto = PhotoBase & {
  type: 'FlatLay';
};

type LifestylePhoto = PhotoBase & {
  type: 'Lifestyle';
};

type RenderPhoto = PhotoBase & {
  type: 'Render';
};

type ProductFeaturePhoto = Pick<PhotoBase, 'src'> & {
  type: 'ProductFeature';
};

type FabricPhoto = PhotoBase & {
  type: 'Fabric';
  color: Color;
};

type ZoomInPhoto = PhotoBase & {
  type: 'ZoomIn';
  color: Color;
};

interface Video {
  wistiaId: string;
  color?: Color;
}

export interface ProductAssets {
  genericPhotos: string[];
  modelPhotos: ModelPhoto[];
  flatLayPhotos: FlatLayPhoto[];
  lifestylePhotos: LifestylePhoto[];
  mannequinPhotos: MannequinPhoto[];
  renderPhotos: RenderPhoto[];
  videos: Video[];
  productFeaturePhotos: ProductFeaturePhoto[];
  fabricPhotos: FabricPhoto[];
  zoomInPhotos: ZoomInPhoto[];
}

// eg:
// 'SPS-FlatLay-Vamp-XL-Emma-Side.png',
// 'SPS-Model-Astral-3X-AJ-Side.png',
// 'SPS-Render-Spicy.png',
// 'SPS-Mannequin-Runway.png',
// 'SPS-Lifestyle-Sand.png',
// 'EGC-Render.png'
// 'SPS-Fabric-Runway.png'
// 'SPS-ZoomIn-Sand.png'

const removeFileTypeIfExists = (maybeHasFileName: string): string => {
  const indexOfPeriod = maybeHasFileName.indexOf('.');
  if (indexOfPeriod > 0) {
    return maybeHasFileName.substring(0, indexOfPeriod);
  }

  return maybeHasFileName;
};

const removeCdnIfExists = (maybeHasCdn: string): string => {
  return maybeHasCdn.substring(maybeHasCdn.lastIndexOf('/') + 1);
};

const extractShortHandle = (imageNameParts: string[]): string => {
  const shortHandle = removeCdnIfExists(imageNameParts[0]);

  return shortHandle;
};

const extractPhotoType = (imageNameParts: string[]): PhotoType => {
  const photoType = removeFileTypeIfExists(imageNameParts[1]);

  if (isPhotoType(photoType)) {
    return photoType;
  }

  throw new Error(`Unable to parse photo type from ${imageNameParts.join('-')}`);
};

const extractColor = (imageNameParts: string[]): Color => {
  const color = removeFileTypeIfExists(imageNameParts[2]);
  if (isColor(color)) {
    return color;
  }

  throw new Error(`Unable to parse color from ${imageNameParts.join('-')}`);
};

const extractShortSize = (imageNameParts: string[]): ShortSize => {
  const shortSize = removeFileTypeIfExists(imageNameParts[3]);
  if (isShortSize(shortSize)) {
    return shortSize;
  }

  throw new Error(`Unable to parse ShortSize from ${imageNameParts.join('-')}`);
};

const extractModelName = (imageNameParts: string[]): ModelName => {
  const modelName = removeFileTypeIfExists(imageNameParts[4]);
  if (isModelName(modelName)) {
    return modelName;
  }

  throw new Error(`Unable to parse Model Name from ${imageNameParts.join('-')}`);
};

const extractDirection = (imageNameParts: string[], index = 5): Direction => {
  const direction = removeFileTypeIfExists(imageNameParts[index]);

  if (isDirection(direction)) {
    return direction;
  }

  throw new Error(`Unable to parse direction from ${imageNameParts.join('-')}`);
};

// const imageRegex = /.*\/(.*)\.(jpg|jpeg|png)/gi;
const extractSrc = (imageName: string): string => {
  // const matches = Array.from(imageName.matchAll(imageRegex))?.[0];

  // if (matches?.[1] && matches?.[2]) {
  //   return `${matches[1]}.${matches[2]}`;
  // }

  // return 'FFFFFF-1.png';
  return imageName;
};

const logImageError = (imageName: string) => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn('Invalid image name:', imageName);
    // window.Sentry.captureException(`Error: Unable to parse image parts for ${imageName}`);
  }
};

const parts = (imageName: string) => imageName.replace(/_/g, ' ').split('-');

const hasColorFn = (type: PhotoType, shortHandle: string): boolean => {
  switch (true) {
    case type === PhotoType.ProductFeature:
      return false;
    case shortHandle === 'EGC':
      return false;
    default:
      return true;
  }
};

const photoProperties = (imageName: string): (PhotoBase & { type: PhotoType }) | undefined => {
  const _parts = parts(imageName);

  try {
    const type = extractPhotoType(_parts);
    const shortHandle = extractShortHandle(_parts);
    const hasColor = hasColorFn(type, shortHandle);

    return {
      src: extractSrc(imageName),
      type,
      color: hasColor ? extractColor(_parts) : undefined,
    };
  } catch (err) {
    logImageError(imageName);
    return undefined;
  }
};

type None = undefined & { _brand: 'NONE' };
const NONE = undefined as None;
const wistiaIds: Record<ProductHandle, { mobile: string; desktop: string } | None> = {
  [ProductHandle.EGiftCard]: NONE,
  [ProductHandle.IntimatesBag]: NONE,
  [ProductHandle.TapeMeasure]: NONE,
  [ProductHandle.WaistHeroCincher]: NONE,
  [ProductHandle.EverydayCottonThong]: NONE,
  [ProductHandle.EverydayCottonBrief]: NONE,
  [ProductHandle.SculptwaveCapri]: NONE,
  [ProductHandle.SilhouetteBra]: { mobile: '6tc6393s7k', desktop: 'l36v657z4l' },
  [ProductHandle.SilhouetteBrief]: { mobile: '6tc6393s7k', desktop: 'l36v657z4l' },
  [ProductHandle.SuperPowerShort]: { mobile: 'b7wfc47w15', desktop: 'vens7a1u5e' },
  [ProductHandle.SuperPowerShortV2]: { mobile: 'b7wfc47w15', desktop: 'vens7a1u5e' },
  [ProductHandle.SuperPowerBrief]: { mobile: 'ej4f7fu58y', desktop: 'wszb7nj6mg' },
  [ProductHandle.QueenBrief]: { mobile: 'edpdjc04oc', desktop: '6ykp4qht46' },
  [ProductHandle.SuperPowerThong]: { mobile: 'jkt3h1odh9', desktop: 'hr75wsivoc' },
  [ProductHandle.LiftWearTank]: { mobile: 'tiaa5f44iy', desktop: 'vktz04kxia' },
  [ProductHandle.LiftWearCami]: { mobile: 'tiaa5f44iy', desktop: 'vktz04kxia' },
  [ProductHandle.CrossoverBra]: { mobile: 'cbqem3b0di', desktop: '5y7sg0ml8y' },
  [ProductHandle.BoldnessTank]: { mobile: '6q3x67uuek', desktop: 'il7z31smly' },
  [ProductHandle.VNeckBra]: { mobile: 'g5k8ljd8pg', desktop: 'gdpwaoesfo' },
  [ProductHandle.EverReadyPant]: { mobile: 'muquh4299a', desktop: '64s3jlp8p6' },
  [ProductHandle.CamiBodysuit]: { mobile: 'qbflskgwxg', desktop: 'w4zlki6nu9' },
  [ProductHandle.LeggingTwo]: { mobile: '3ihi3sfnjc', desktop: 'ezkmsxsn7j' },
  [ProductHandle.BoldnessBodysuit]: { mobile: 'r82utkozpe', desktop: '3mxjj4n198' },
  [ProductHandle.TankBodysuit]: { mobile: 'zqjzf3fzyf', desktop: 'ac45o2erd9' },
  [ProductHandle.WaistHeroCincher]: { mobile: '3tfpt9e1fd', desktop: 'gqoymkzs8t' },
};

const productVideo = (product: ViewProduct, isMobile: boolean): Video | undefined => {
  const ids = wistiaIds[product.handle];
  const wistiaId = isMobile ? ids?.mobile : ids?.desktop;

  return wistiaId ? { wistiaId } : undefined;
};

// Force the cache key to be generated via this function via typescript branding
type CacheKey = string & { __cacheKeyBrand: void };
const cacheKey = (productId: string, isMobile: boolean): CacheKey => `${productId}-${isMobile}` as CacheKey;
const assetCache: Record<CacheKey, ProductAssets> = {};

export const useProductAssets = (product: ViewProduct | undefined): ProductAssets => {
  const isMobile = useRecoilValue(isMobileState);
  const initialAssets: ProductAssets = {
    flatLayPhotos: [],
    genericPhotos: [],
    lifestylePhotos: [],
    mannequinPhotos: [],
    modelPhotos: [],
    renderPhotos: [],
    productFeaturePhotos: [],
    videos: [],
    fabricPhotos: [],
    zoomInPhotos: [],
  };

  if (!product) {
    return initialAssets;
  }

  const cached = assetCache[cacheKey(product.id, isMobile)];
  if (cached) {
    return cached;
  }

  const pdpVideo = productVideo(product, isMobile);
  initialAssets.videos = pdpVideo ? [pdpVideo] : [];

  const productAssets = product.images.reduce((acc, curr) => {
    const properties = photoProperties(curr);
    if (!properties) {
      acc.genericPhotos.push(extractSrc(curr));
      return acc;
    }

    const { type, ...attributes } = properties;
    const basePhoto: PhotoBase = {
      color: attributes.color,
      src: attributes.src,
    };
    switch (type) {
      case PhotoType.Mannequin:
        try {
          const mannequinPhoto: MannequinPhoto = {
            ...basePhoto,
            type: PhotoType.Mannequin,
            direction: extractDirection(parts(curr), 3),
          };
          acc.mannequinPhotos.push(mannequinPhoto);
        } catch (error) {
          logImageError(curr);
          acc.genericPhotos.push(basePhoto.src);
        }
        break;
      case PhotoType.FlatLay:
        const flatLayPhoto: FlatLayPhoto = {
          ...basePhoto,
          type: PhotoType.FlatLay,
        };
        acc.flatLayPhotos.push(flatLayPhoto);
        break;
      case PhotoType.Render:
        const renderPhoto: RenderPhoto = {
          ...basePhoto,
          type: PhotoType.Render,
        };
        acc.renderPhotos.push(renderPhoto);
        break;
      case PhotoType.Model:
        try {
          const modelPhoto: ModelPhoto = {
            ...basePhoto,
            type: PhotoType.Model,
            modelName: extractModelName(parts(curr)),
            direction: extractDirection(parts(curr)),
            size: extractShortSize(parts(curr)),
          };
          acc.modelPhotos.push(modelPhoto);
        } catch (error) {
          logImageError(curr);
          acc.genericPhotos.push(basePhoto.src);
        }
        break;
      case PhotoType.Lifestyle:
        const lifestylePhoto: LifestylePhoto = {
          ...basePhoto,
          type: PhotoType.Lifestyle,
        };
        acc.lifestylePhotos.push(lifestylePhoto);
        break;
      case PhotoType.ProductFeature:
        const productFeaturePhoto: ProductFeaturePhoto = {
          ...basePhoto,
          type: PhotoType.ProductFeature,
        };
        acc.productFeaturePhotos.push(productFeaturePhoto);
        break;
      case PhotoType.Fabric:
        const fabricPhoto: FabricPhoto = {
          ...basePhoto,
          type: PhotoType.Fabric,
          color: extractColor(parts(curr)),
        };
        acc.fabricPhotos.push(fabricPhoto);
        break;
      case PhotoType.ZoomIn:
        const zoomInPhoto: ZoomInPhoto = {
          ...basePhoto,
          type: PhotoType.ZoomIn,
          color: extractColor(parts(curr)),
        };
        acc.zoomInPhotos.push(zoomInPhoto);
        break;
      default:
        acc.genericPhotos.push(basePhoto.src);
        break;
    }

    return acc;
  }, initialAssets);

  assetCache[cacheKey(product.id, isMobile)] = productAssets;
  return productAssets;
};
